import { type FC } from "react";
import { X, Check, AlertCircle, Maximize, Focus, Camera } from "lucide-react";
import { Button } from "../../components/ui/button";

interface BestScanningPracticesProps {
  onClose: () => void;
  onScanNow: () => void;
}

export const BestScanningPractices: FC<BestScanningPracticesProps> = ({
  onClose,
  onScanNow,
}) => {
  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-black/80 backdrop-blur-sm safe-area-inset-all">
      <style>{`
        /* Hide scrollbar for iOS/Safari */
        .best-practices-modal::-webkit-scrollbar {
          display: none;
        }
        .best-practices-scroll::-webkit-scrollbar {
          display: none;
        }
        /* Smooth scrolling on iOS */
        .best-practices-scroll {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
      {/* Close Button - Safe area aware */}
      <button
        onClick={onClose}
        className="fixed right-2 sm:right-4 z-[110] flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20 active:scale-95"
        style={{ top: 'max(8px, env(safe-area-inset-top))', right: 'max(8px, env(safe-area-inset-right))' }}
        aria-label="Close"
      >
        <X size={20} className="sm:hidden" />
        <X size={24} className="hidden sm:block" />
      </button>

      {/* Scroll container - ensures the dialog is fully reachable on small screens */}
      <div
        className="best-practices-scroll flex min-h-full items-center justify-center p-2 sm:p-4"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
      {/* Main Content - iOS optimized */}
      <div 
        className="best-practices-modal w-full max-w-[440px] overflow-y-auto overscroll-contain rounded-2xl sm:rounded-3xl bg-card shadow-2xl animate-in fade-in zoom-in duration-300"
        style={{ 
          maxHeight: 'calc(100dvh - max(16px, env(safe-area-inset-top)) - max(16px, env(safe-area-inset-bottom)))',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-border">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground pr-6">
            Best scanning practices
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">
            Follow these tips for better food recognition
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
          {/* Do's and Don'ts Section */}
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            {/* Do Column */}
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-green-500">
                  <Check size={14} className="text-white sm:hidden" />
                  <Check size={16} className="text-white hidden sm:block" />
                </div>
                <span className="text-sm sm:text-base font-semibold text-foreground">Do</span>
              </div>

              {/* Do Example - Clean Plate */}
              <div className="rounded-lg sm:rounded-xl border-2 border-green-500/20 bg-green-50 dark:bg-green-950/20 p-2 sm:p-3 overflow-hidden">
                <div className="aspect-[3/4] rounded-md sm:rounded-lg relative overflow-hidden bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                  {/* Phone mockup background */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: "url('https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=600&fit=crop')",
                      filter: "brightness(0.95)"
                    }}
                  />
                  {/* Scan frame overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-[75%] h-[70%] border-2 sm:border-4 border-white/90 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl" 
                         style={{ 
                           boxShadow: '0 0 0 1px rgba(34, 197, 94, 0.3), inset 0 0 10px rgba(0,0,0,0.1)' 
                         }}
                    />
                  </div>
                  {/* Green check indicator with pulse */}
                  <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-green-500 rounded-full p-0.5 sm:p-1 animate-pulse">
                    <Check size={10} className="text-white sm:hidden" />
                    <Check size={12} className="text-white hidden sm:block" />
                  </div>
                  {/* Success badge */}
                  <div className="absolute bottom-2 left-2 right-2 bg-green-500/90 backdrop-blur-sm rounded-md px-2 py-1">
                    <p className="text-2xs sm:text-compact-xs text-white font-semibold text-center">✓ Good Example</p>
                  </div>
                </div>
                <p className="mt-1.5 sm:mt-2 text-compact-xs sm:text-xs text-center text-green-700 dark:text-green-300 font-medium leading-tight">
                  Clean single dish<br/>in scan frame
                </p>
              </div>
            </div>

            {/* Don't Column */}
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-red-500">
                  <X size={14} className="text-white sm:hidden" />
                  <X size={16} className="text-white hidden sm:block" />
                </div>
                <span className="text-sm sm:text-base font-semibold text-foreground">Don't</span>
              </div>

              {/* Don't Example - Messy/Obscured */}
              <div className="rounded-lg sm:rounded-xl border-2 border-red-500/20 bg-red-50 dark:bg-red-950/20 p-2 sm:p-3 overflow-hidden">
                <div className="aspect-[3/4] rounded-md sm:rounded-lg relative overflow-hidden bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                  {/* Phone mockup background with messy food */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: "url('https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=600&fit=crop')",
                      filter: "brightness(0.8) blur(1px)"
                    }}
                  />
                  {/* Scan frame overlay - food outside */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-[50%] h-[45%] border-2 sm:border-4 border-white/90 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl" 
                         style={{ 
                           boxShadow: '0 0 0 1px rgba(239, 68, 68, 0.3), inset 0 0 10px rgba(0,0,0,0.2)' 
                         }}
                    />
                  </div>
                  {/* Obscured overlay elements */}
                  <div className="absolute top-2 right-2 sm:top-4 sm:right-4 w-12 h-14 sm:w-16 sm:h-20 bg-white/40 backdrop-blur-sm rounded-md sm:rounded-lg rotate-12 border border-white/60 sm:border-2" />
                  <div className="absolute bottom-3 left-2 sm:bottom-6 sm:left-4 w-14 h-12 sm:w-20 sm:h-16 bg-white/30 backdrop-blur-sm rounded-md sm:rounded-lg -rotate-6 border border-white/60 sm:border-2" />
                  {/* Red X indicator with pulse */}
                  <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-red-500 rounded-full p-0.5 sm:p-1 animate-pulse">
                    <X size={10} className="text-white sm:hidden" />
                    <X size={12} className="text-white hidden sm:block" />
                  </div>
                  {/* Error badge */}
                  <div className="absolute bottom-2 left-2 right-2 bg-red-500/90 backdrop-blur-sm rounded-md px-2 py-1">
                    <p className="text-2xs sm:text-compact-xs text-white font-semibold text-center">✗ Avoid This</p>
                  </div>
                </div>
                <p className="mt-1.5 sm:mt-2 text-compact-xs sm:text-xs text-center text-red-700 dark:text-red-300 font-medium leading-tight">
                  Obscured or<br/>outside frame
                </p>
              </div>
            </div>
          </div>

          {/* General Tips Section */}
          <div className="space-y-2 sm:space-y-3 pt-2">
            <h3 className="text-sm sm:text-base font-semibold text-foreground flex items-center gap-1.5 sm:gap-2">
              <AlertCircle size={16} className="text-primary sm:hidden" />
              <AlertCircle size={18} className="text-primary hidden sm:block" />
              General tips:
            </h3>
            <ul className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm text-foreground/90">
              <li className="flex items-start gap-2.5 sm:gap-3 bg-muted/50 rounded-lg p-2 sm:p-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Maximize size={14} className="text-primary sm:hidden" />
                    <Maximize size={16} className="text-primary hidden sm:block" />
                  </div>
                </div>
                <span className="flex-1">Keep the food <strong>inside the scan lines</strong></span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3 bg-muted/50 rounded-lg p-2 sm:p-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Focus size={14} className="text-primary sm:hidden" />
                    <Focus size={16} className="text-primary hidden sm:block" />
                  </div>
                </div>
                <span className="flex-1">Hold your phone <strong>still</strong> so the image is not blurry</span>
              </li>
              <li className="flex items-start gap-2.5 sm:gap-3 bg-muted/50 rounded-lg p-2 sm:p-2.5">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <Camera size={14} className="text-primary sm:hidden" />
                    <Camera size={16} className="text-primary hidden sm:block" />
                  </div>
                </div>
                <span className="flex-1">Don't take the picture at <strong>obscure angles</strong></span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer with Scan Now Button - iPhone safe area */}
        <div 
          className="sticky bottom-0 bg-card px-4 sm:px-6 pt-3 sm:pt-4 border-t border-border"
          style={{ 
            paddingBottom: 'max(16px, calc(env(safe-area-inset-bottom) + 8px))' 
          }}
        >
          <Button
            className="w-full rounded-full py-5 sm:py-6 text-sm sm:text-base font-semibold bg-foreground text-background hover:bg-foreground/90 active:scale-[0.98] transition-transform shadow-lg"
            onClick={onScanNow}
          >
            <Camera className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            Scan now
          </Button>
          <p className="text-center text-compact-xs sm:text-xs text-muted-foreground mt-2">
            Tap to start scanning your food
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default BestScanningPractices;
